import { css, CSSObject, FlattenInterpolation, Interpolation, ThemedStyledProps } from 'styled-components';

export interface Breakpoints {
  values: typeof breakpoints;
  queries: {
    isDesktop: string;
    isMobile: string;
    isTablet: string;
    isMiddleExtraSmall: string;
  };
}

const breakpoints = {
  df: 0,
  xs: 360,
  xsm: 480,
  sm: 768,
  md: 1024,
  lg: 1280,
  lm: 1400,
};

type StyledFunc = <Props extends {}>(
  first: TemplateStringsArray | CSSObject,
  ...interpolations: Array<Interpolation<ThemedStyledProps<Props, Theme>>>
) => FlattenInterpolation<any>;

const breakpointKeys = Object.keys(breakpoints) as (keyof typeof breakpoints)[];
type BreakpointsSet = { [key in keyof typeof breakpoints]: StyledFunc };

export const breakpoint = {
  up: breakpointKeys.reduce<BreakpointsSet>((acc, label) => {
    acc[label] = (first, ...interpolations) => css`
      @media (min-width: ${breakpoints[label]}px) {
        ${css(first, ...interpolations)}
      }
    `;
    return acc;
  }, {} as BreakpointsSet),
  down: breakpointKeys.reduce<BreakpointsSet>((acc, label) => {
    acc[label] = (...values) => css`
      @media (max-width: ${breakpoints[label]}px) {
        ${css(...values)}
      }
    `;
    return acc;
  }, {} as BreakpointsSet),
};

export const color = {
  white: '#fff',
  brickRed: '#B62B45',
  lightningYellow: '#F8C326',

  green: '#09a96c',

  gray: {
    primary: '#464646',
    dividerGray: '#D3D3D3',
    dustyGray: '#999999',
    doveGray: '#666666',
    silverChalice: '#A3A3A3',
    silver: '#C4C4C4',
    gallery: '#EDEDED',
    mercury: '#e5e5e5',
    nevada: '#616364',
    mineShaft: '#333333',
    policyBackground: '#FAFAFA',
  },
  black: {
    primary: '#000',
    blackcurrant: '#32293B',
    black06: 'rgba(0, 0, 0, 0.6)',
  },
};

export interface Theme {
  palette: {
    primary: string;
    secondary: string;
    error: string;
    progressBar: string;
    gray: {
      primary: string;
      dividerGray: string;
      dustyGray: string;
      doveGray: string;
      silverChalice: string;
      silver: string;
      gallery: string;
      mercury: string;
      nevada: string;
      mineShaft: string;
      policyBackground: string;
    };
    black: {
      primary: string;
      black06: string;
      blackcurrant: string;
    };
    white: string;
  };
  breakpoints: Breakpoints;
  zIndex: {
    appHeader: {
      mobile: number;
      desktop: number;
    };
    snackbar: number;
    menuList: number;
    popup: number;
  };
}

export const theme: Theme = {
  palette: {
    primary: color.brickRed,
    secondary: color.lightningYellow,
    progressBar: color.green,
    error: color.brickRed,
    white: color.white,
    gray: color.gray,
    black: color.black,
  },
  breakpoints: {
    values: breakpoints,
    queries: {
      isMobile: `(max-width: ${breakpoints.sm}px)`,
      isTablet: `(max-width: ${breakpoints.md}px) and (min-width:${breakpoints.sm}px)`,
      isDesktop: `(min-width: ${breakpoints.md}px)`,
      isMiddleExtraSmall: `(max-width: ${breakpoints.xsm}px) and (min-width: ${breakpoints.df}px)`,
    },
  },
  zIndex: {
    appHeader: {
      mobile: 1002,
      desktop: 1,
    },
    snackbar: 1000,
    menuList: 1000,
    popup: 10,
  },
};
